import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, OnDestroy, Output, OnInit, Input, SimpleChanges, OnChanges } from '@angular/core';
import Conference from '@voxeet/voxeet-web-sdk/types/models/Conference';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { combineLatest, Subscription } from 'rxjs';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { MuteService } from '../../services/mute/mute.service';
import { NameService } from '../../services/name/name.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { StatsService } from '../../services/stats/stats.service';
import { UserService } from '../../services/user/user.service';
import { VideoOffService } from '../../services/video-off/video-off.service';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { SCSubject } from '../../util/sc-subject.class';

@Component({
  selector: 'sc-preview-test',
  templateUrl: './preview-test.component.html',
  styleUrls: ['./preview-test.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(100%)' }),
        animate('100ms', style({ opacity: 1, transform: 'translateX(0%)' })),
      ]),
      transition(':leave', [animate('100ms', style({ opacity: 0, transform: 'translateY(-100%)' }))]),
    ]),
  ],
})
export class PreviewTestComponent implements OnInit, OnDestroy, OnChanges {
  @Input() joining = false;
  @Output() testResults = new EventEmitter();
  @Output() shutdown = new EventEmitter();

  user$ = this.userService.activeUser$;
  participants$ = this.sessionsService.participants$;
  studioSession$ = this.sessionsService.studioSession$;
  videoOff$ = this.videoOffService.videoOff$;
  mute$ = this.muteService.mute$;
  connectionStats$ = this.statsService.connectionStats$;
  name$ = new SCSubject<string>();
  sampleRateToast: any = null;
  showStats = false;
  netStats = {
    qualityLimitationReason: 'none',
    availableBitrate: 0,
    serverPing: 0,
    mos: 0,
  };
  audioStats: any = {};
  videoStats: any = [];

  videoStream: MediaStream;
  audioStream: MediaStream;
  testConvo: Conference;

  subs: Array<Subscription> = [];

  constructor(
    private analyticsService: AnalyticsService,
    private deviceDetectorService: DeviceDetectorService,
    private equipmentService: EquipmentService,
    private muteService: MuteService,
    private nameService: NameService,
    private sessionsService: SessionsService,
    private statsService: StatsService,
    private toastrService: ToastrService,
    private userService: UserService,
    private videoOffService: VideoOffService
  ) {}

  async ngOnInit() {
    const sub = this.connectionStats$.subscribe((stats) => {
      this.videoStats = [];
      this.netStats.availableBitrate = stats.network.availableOutgoingBitrate;
      this.netStats.serverPing = stats.network.currentRoundTripTime * 1000;

      if (stats.scores?.video) this.netStats.mos = Math.round(stats.scores.video);
      else if (stats.scores?.audio) this.netStats.mos = Math.round(stats.scores.audio);

      Object.keys(stats.audio).forEach((ssrc) => {
        this.audioStats.bitrate = stats.audio[ssrc].bitrate;
        this.audioStats.jitter = stats.audio[ssrc].remote?.jitter * 1000;
        this.audioStats.packetsLost = stats.audio[ssrc].remote?.packetsLost;
      });
      Object.keys(stats.video).forEach((ssrc) => {
        const vidStat = stats.video[ssrc];
        const active = vidStat.active;
        const bitrate = vidStat.bitrate;
        const jitter = vidStat.remote?.jitter * 1000;
        const packetsLost = vidStat.remote?.packetsLost;
        const frameHeight = vidStat.frameHeight;
        const frameWidth = vidStat.frameWidth;
        const framesPerSecond = vidStat.framesPerSecond;
        if (vidStat.qualityLimitationReason) this.netStats.qualityLimitationReason = vidStat.qualityLimitationReason;
        this.videoStats.push({ active, ssrc, bitrate, jitter, packetsLost, frameHeight, frameWidth, framesPerSecond });
      });
    });
    this.subs.push(sub);
    this.setupName();
    this.setupMediaStream();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.joining.currentValue) {
      this.audioStream?.getTracks().forEach((track) => track.stop());
      this.videoStream?.getTracks().forEach((track) => track.stop());
      this.shutdown.emit();
    }
  }

  async setupMediaStream() {
    const sub = combineLatest([this.equipmentService.constraints$, this.videoOff$]).subscribe(
      async ([constraints, videoOff]) => {
        if (this.videoStream && videoOff) this.videoStream.getTracks().forEach((track) => track.stop());
        if (constraints?.video && !videoOff) {
          this.videoStream?.getTracks().forEach((track) => track.stop());
          this.videoStream = await navigator.mediaDevices.getUserMedia({ audio: false, video: constraints.video });
        }
        this.equipmentService.setEquipmentMetrics(this.audioStream?.getTracks()[0], this.videoStream?.getTracks()[0]);
      }
    );
    this.subs.push(sub);

    const sub2 = this.equipmentService.constraints$.subscribe(async (constraints) => {
      if (this.audioStream) this.audioStream.getTracks().forEach((track) => track.stop());
      if (constraints?.audio) {
        this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: constraints.audio, video: false });
      }
      this.equipmentService.setEquipmentMetrics(this.audioStream?.getTracks()[0], this.videoStream?.getTracks()[0]);
    });
    this.subs.push(sub2);
  }

  async runSampleRateCheck() {
    if (this.deviceDetectorService.isMobile()) return;

    const sampleRate: number = this.audioStream.getAudioTracks()[0].getSettings().sampleRate;

    if (!sampleRate || this.sampleRateToast) {
      return;
    }

    if (sampleRate !== 44100 && sampleRate !== 48000) {
      const sampleRateToast: ActiveToast<GeneralToastComponent> = this.toastrService.info(
        `Please test the audio in your Recording Session to ensure everyone can hear you clearly`,
        `Your Microphone may need some settings adjusted`,
        {
          progressBar: false,
          closeButton: true,
          tapToDismiss: false,
          disableTimeOut: true,
          toastComponent: GeneralToastComponent,
        }
      );

      sampleRateToast.toastRef.componentInstance.learnMoreTopic = 'micSampleRate';
      await this.analyticsService.track('opened sample rate warning');
    }
  }

  setupName() {
    this.nameService.getName(this.studioSession$.value.sessionID, this.user$.value.uid).subscribe((name) => {
      this.name$.next(name);
    });
  }

  ngOnDestroy() {
    if (this.videoStream) this.videoStream.getTracks().forEach((track) => track.stop());
    if (this.audioStream) this.audioStream.getTracks().forEach((track) => track.stop());
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
